// src/dashboard/Users.jsx

import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { AuthContext } from '../context/AuthContext';
import { useIntl } from 'react-intl';
import { PlusCircle, Filter, Users as UsersIcon, X } from 'lucide-react';
import UserTable from '../components/users/UserTable';
import AddUserModal from '../components/users/AddUserModal';
import { toast } from 'react-toastify';

export default function Users() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Carregar usuários (escopo do cliente) em tempo real
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);

    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('parentId', '==', user.uid));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching users:', error);
        setLoading(false);
        toast.error(
          intl.formatMessage({
            id: 'users.fetchError',
            defaultMessage: 'Não foi possível carregar os usuários.',
          })
        );
      }
    );

    return () => unsubscribe();
  }, [user?.uid, intl]);

  // Aplicar filtros em memória
  const filteredUsers = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      const searchMatch = !s || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(s);
      const roleMatch = filterRole === 'all' || (u.role || '') === filterRole;
      const statusMatch =
        filterStatus === 'all' ||
        (filterStatus === 'active' ? Boolean(u.active) : !Boolean(u.active));
      return searchMatch && roleMatch && statusMatch;
    });
  }, [users, searchTerm, filterRole, filterStatus]);

  // Handlers — o onSnapshot atualiza a UI automaticamente
  const handleUserUpdate = useCallback(() => {}, []);
  const handleUserDeleted = useCallback(() => {}, []);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterRole('all');
    setFilterStatus('all');
  };

  return (
    <div className="la dashboard-page">
      <div className="dashboard-content">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <h2 className="page-title">
              {intl.formatMessage({ id: 'menu.profile-users', defaultMessage: 'Users' })}
            </h2>
            <div className="page-subtitle">
              <UsersIcon size={16} style={{ marginRight: 6 }} />
              <span>
                {intl.formatMessage(
                  { id: 'users.count', defaultMessage: '{count} users' },
                  { count: filteredUsers.length }
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="filters-container">
          <div className="filters-row">
            <div className="filters-left">
              <div className="filter-icon">
                <Filter size={16} />
              </div>

              <input
                type="text"
                className="input-field"
                placeholder={intl.formatMessage({
                  id: 'users.searchPlaceholder',
                  defaultMessage: 'Buscar por nome ou e-mail...',
                })}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label={intl.formatMessage({
                  id: 'users.searchPlaceholder',
                  defaultMessage: 'Buscar por nome ou e-mail...',
                })}
              />

              <select
                className="input-field"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                aria-label={intl.formatMessage({
                  id: 'users.filterByType',
                  defaultMessage: 'Filtrar por tipo',
                })}
              >
                <option value="all">
                  {intl.formatMessage({
                    id: 'users.filter.allTypes',
                    defaultMessage: 'Todos os Tipos',
                  })}
                </option>
                <option value="corretor">
                  {intl.formatMessage({ id: 'user.role.corretor', defaultMessage: 'Corretor' })}
                </option>
                <option value="editor">
                  {intl.formatMessage({ id: 'user.role.editor', defaultMessage: 'Editor' })}
                </option>
                <option value="suporte">
                  {intl.formatMessage({ id: 'user.role.suporte', defaultMessage: 'Suporte' })}
                </option>
              </select>

              <select
                className="input-field"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                aria-label={intl.formatMessage({
                  id: 'users.filterByStatus',
                  defaultMessage: 'Filtrar por status',
                })}
              >
                <option value="all">
                  {intl.formatMessage({
                    id: 'users.filter.allStatuses',
                    defaultMessage: 'Todos os Status',
                  })}
                </option>
                <option value="active">
                  {intl.formatMessage({ id: 'user.active', defaultMessage: 'Ativo' })}
                </option>
                <option value="inactive">
                  {intl.formatMessage({ id: 'user.inactive', defaultMessage: 'Inativo' })}
                </option>
              </select>
            </div>

            <div className="filters-right">
              {(searchTerm || filterRole !== 'all' || filterStatus !== 'all') && (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={clearFilters}
                  aria-label={intl.formatMessage({
                    id: 'users.clearFilters',
                    defaultMessage: 'Limpar filtros',
                  })}
                >
                  <X size={14} style={{ marginRight: 6 }} />
                  {intl.formatMessage({
                    id: 'users.clearFilters',
                    defaultMessage: 'Limpar filtros',
                  })}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabela / Estados */}
        {loading ? (
          <p className="muted-text">
            {intl.formatMessage({ id: 'loading.label', defaultMessage: 'Carregando...' })}
          </p>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <UsersIcon size={20} />
            </div>
            <h3 className="empty-title">
              {intl.formatMessage({
                id: 'users.emptyTitle',
                defaultMessage: 'Nenhum usuário encontrado',
              })}
            </h3>
            <p className="empty-subtitle">
              {intl.formatMessage({
                id: 'users.emptySubtitle',
                defaultMessage:
                  'Ajuste os filtros ou adicione um novo usuário para começar a montar sua equipe.',
              })}
            </p>
            <button
              className="primary-button"
              type="button"
              onClick={() => setIsAddModalOpen(true)}
            >
              <PlusCircle size={16} style={{ marginRight: 8 }} />
              {intl.formatMessage({
                id: 'user.addNewToTeam',
                defaultMessage: 'Adicionar novo usuário',
              })}
            </button>
          </div>
        ) : (
          <UserTable
            users={filteredUsers}
            onUserUpdate={handleUserUpdate}
            onUserDeleted={handleUserDeleted}
          />
        )}
      </div>

      {/* Modal: Adicionar usuário */}
      {isAddModalOpen && (
        <AddUserModal
          onClose={() => setIsAddModalOpen(false)}
          onUserAdded={(newUser) => {
            toast.success(
              intl.formatMessage(
                {
                  id: 'user.addedSuccess',
                  defaultMessage: 'Usuário {name} adicionado.',
                },
                { name: newUser?.name || '' }
              )
            );
          }}
        />
      )}
    </div>
  );
}
